import { useEffect, useState } from "react";
import { Home, Briefcase, MapPin, Loader2, Plus, Trash2, Star, SquareParking, Route, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { geocodeAddress } from "@/lib/geo";

const ICON = { home: Home, work: Briefcase, parking: SquareParking };
const iconFor = (label) => ICON[(label || "").toLowerCase().split(" ")[0]] || MapPin;

export function useSavedPlaces() {
  const [places, setPlaces] = useState([]);
  const load = () => api.get("/me/saved-places").then((r) => setPlaces(r.data.places || [])).catch(() => {});
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("saved-places-updated", h);
    return () => window.removeEventListener("saved-places-updated", h);
  }, []);
  return { places, reload: load };
}

export function useRecentPlaces() {
  const [recent, setRecent] = useState([]);
  const load = () => api.get("/me/recent-places").then((r) => setRecent(r.data.places || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  return { recent, reload: load };
}

// Record a used pickup/drop so it shows up under "Recent" next time (fire-and-forget).
export const recordRecentPlace = (address, coords = {}) => {
  if (!address || !address.trim()) return;
  api.post("/me/recent-places", { address: address.trim(), lat: coords.lat ?? null, lng: coords.lng ?? null }).catch(() => {});
};

// Profile section: manage Home / Work / Parking / custom places AND Home→Work favourite routes.
export function SavedPlacesManager() {
  const { places, reload } = useSavedPlaces();
  const [routeMode, setRouteMode] = useState(false);
  const [label, setLabel] = useState("");
  const [address, setAddress] = useState("");
  const [toAddress, setToAddress] = useState("");
  const [saving, setSaving] = useState(false);

  const quick = (l) => {
    let next = l;
    if (l === "Parking") next = `Parking ${places.filter((p) => (p.label || "").toLowerCase().startsWith("parking")).length + 1}`;
    setRouteMode(false); setLabel(next); document.getElementById("saved-place-address")?.focus();
  };

  const save = async () => {
    const lb = label.trim(), ad = address.trim();
    if (!lb || !ad) { toast.error("Add a label and an address."); return; }
    if (routeMode && !toAddress.trim()) { toast.error("A route needs a destination too."); return; }
    setSaving(true);
    try {
      const geo = await geocodeAddress(ad).catch(() => null);
      const body = { label: lb, address: ad, lat: geo?.lat ?? null, lng: geo?.lng ?? null, kind: routeMode ? "route" : "place" };
      if (routeMode) { const g2 = await geocodeAddress(toAddress.trim()).catch(() => null); body.to_address = toAddress.trim(); body.to_lat = g2?.lat ?? null; body.to_lng = g2?.lng ?? null; }
      await api.post("/me/saved-places", body);
      toast.success(routeMode ? "Route saved — one tap fills both ends." : (geo ? "Place saved — location pinned for exact ETAs." : "Place saved."));
      setLabel(""); setAddress(""); setToAddress(""); setRouteMode(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/me/saved-places/${id}`); reload(); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div data-testid="saved-places-manager">
      <p className="text-sm text-slate-500">Save Home, Work, parking spots and Home→Work routes so pickup &amp; drop fill instantly on cab, taxi, auto &amp; bike rides — with exact ETAs. Add as many as you need.</p>
      <div className="mt-4 space-y-2" data-testid="saved-places-list">
        {places.length === 0 && <p className="text-sm text-slate-400">No saved places yet.</p>}
        {places.map((p) => {
          const isRoute = p.kind === "route";
          const Icon = isRoute ? Route : iconFor(p.label);
          return (
            <div key={p.id} className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 p-3" data-testid={`saved-place-${p.id}`}>
              <div className="flex min-w-0 items-start gap-2.5">
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isRoute ? "bg-violet-50 text-violet-600" : "bg-sky-50 text-sky-600"}`}><Icon className="h-4 w-4" /></span>
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">{p.label}{p.lat != null && <Star className="h-3 w-3 fill-emerald-400 text-emerald-400" />}{isRoute && <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold text-violet-700">ROUTE</span>}</p>
                  <p className="truncate text-xs text-slate-500">{isRoute ? `${p.address} → ${p.to_address}` : p.address}</p>
                </div>
              </div>
              <button onClick={() => remove(p.id)} data-testid={`saved-place-delete-${p.id}`} className="shrink-0 rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:border-rose-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>
      <div className="mt-4 rounded-xl border border-slate-200 p-3" data-testid="saved-places-form">
        <div className="mb-2 flex flex-wrap gap-1.5">
          {[["Home", Home], ["Work", Briefcase], ["Parking", SquareParking]].map(([l, Ic]) => (
            <button key={l} type="button" onClick={() => quick(l)} data-testid={`saved-place-quick-${l.toLowerCase()}`}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${!routeMode && (label === l || (l === "Parking" && label.toLowerCase().startsWith("parking"))) ? "bg-sky-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              <Ic className="h-3 w-3" /> {l}
            </button>
          ))}
          <button type="button" onClick={() => { setRouteMode((v) => !v); if (!routeMode && (label === "" || ["home", "work"].includes(label.toLowerCase()))) setLabel("Home → Work"); }} data-testid="saved-place-quick-route"
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${routeMode ? "bg-violet-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            <Route className="h-3 w-3" /> Route
          </button>
        </div>
        <div className="space-y-2">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={routeMode ? "Route name (e.g. Home → Work)" : "Label (e.g. Gym)"} data-testid="saved-place-label" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
          <input id="saved-place-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder={routeMode ? "Start / pickup address" : "Full address / area / landmark"} data-testid="saved-place-address" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
          {routeMode && <input value={toAddress} onChange={(e) => setToAddress(e.target.value)} placeholder="Destination / drop address" data-testid="saved-place-to" className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />}
        </div>
        <button onClick={save} disabled={saving} data-testid="saved-place-save" className="mt-3 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {routeMode ? "Save route" : "Save place"}
        </button>
      </div>
    </div>
  );
}

// Quick-pick chips for a single pickup/drop field (saved places, kind=place).
export function SavedPlacePicker({ onPick, testid = "saved-place-picker" }) {
  const { places } = useSavedPlaces();
  const list = places.filter((p) => p.kind !== "route");
  if (!list.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={testid}>
      {list.map((p) => {
        const Icon = iconFor(p.label);
        return (
          <button key={p.id} type="button" onClick={() => onPick(p)} data-testid={`${testid}-${p.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-100">
            <Icon className="h-3 w-3" /> {p.label}
          </button>
        );
      })}
    </div>
  );
}

// One-tap favourite routes that fill BOTH pickup and drop.
export function RoutePicker({ onPick, testid = "route-picker" }) {
  const { places } = useSavedPlaces();
  const routes = places.filter((p) => p.kind === "route");
  if (!routes.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={testid}>
      {routes.map((p) => (
        <button key={p.id} type="button" onClick={() => onPick({ from: p.address, to: p.to_address })} data-testid={`${testid}-${p.id}`}
          className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-100">
          <Route className="h-3 w-3" /> {p.label}
        </button>
      ))}
    </div>
  );
}

// Recently used pickup/drop points.
export function RecentPicker({ onPick, testid = "recent-picker" }) {
  const { recent } = useRecentPlaces();
  if (!recent.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={testid}>
      {recent.map((r, i) => (
        <button key={i} type="button" onClick={() => onPick(r.address, r)} data-testid={`${testid}-${i}`}
          className="inline-flex max-w-[220px] items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50">
          <Clock className="h-3 w-3 text-slate-400" /> <span className="truncate">{r.address}</span>
        </button>
      ))}
    </div>
  );
}

// Inline "star → save this pickup/drop as Home / Work / custom" affordance for the ride search.
export function SavePlaceButton({ address, coords, testid = "save-place" }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  if (!address || !address.trim()) return null;
  const doSave = async (lb) => {
    const finalLabel = (lb || label).trim();
    if (!finalLabel) { toast.error("Name this place (e.g. Home, Gym)"); return; }
    setSaving(true);
    try {
      let c = coords;
      if (!(c && c.lat)) c = await geocodeAddress(address).catch(() => null);
      await api.post("/me/saved-places", { label: finalLabel, address: address.trim(), lat: c?.lat ?? null, lng: c?.lng ?? null });
      toast.success(`Saved as “${finalLabel}” — one tap next time`);
      setOpen(false); setLabel("");
      window.dispatchEvent(new Event("saved-places-updated"));
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Log in to save places" : (e?.response?.data?.detail || "Could not save"));
    } finally { setSaving(false); }
  };
  return (
    <div className="relative inline-block">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`${testid}-btn`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 hover:text-amber-700"><Star className="h-3 w-3" /> Save</button>
      {open && (
        <div className="absolute z-40 mt-1 w-48 rounded-xl border border-slate-200 bg-white p-2 shadow-lg" data-testid={`${testid}-menu`}>
          <div className="mb-1.5 flex gap-1">
            <button type="button" onClick={() => doSave("Home")} disabled={saving} data-testid={`${testid}-home`} className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-700 hover:bg-sky-100"><Home className="h-3 w-3" /> Home</button>
            <button type="button" onClick={() => doSave("Work")} disabled={saving} data-testid={`${testid}-work`} className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-700 hover:bg-sky-100"><Briefcase className="h-3 w-3" /> Work</button>
          </div>
          <div className="flex gap-1">
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Custom (e.g. Gym)" data-testid={`${testid}-label`} className="h-7 min-w-0 flex-1 rounded-md border border-slate-200 px-2 text-[11px]" />
            <button type="button" onClick={() => doSave()} disabled={saving} data-testid={`${testid}-save`} className="rounded-md brand-gradient-bg px-2.5 text-[11px] font-semibold text-white disabled:opacity-50">{saving ? "…" : "Save"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
